/**
 * 「另存 JPEG」按鈕(REQ-20260903006)—— 六個列印入口共用同一顆。
 *
 * 需求是「所有列印 / 另存 PDF 的地方都要多一個 JPEG 格式」,六處的行為完全一樣
 * (抓同一塊單據、同一套失敗處理、同一個產生中狀態),所以做成一顆共用按鈕,
 * 而不是把十幾行的 state + handler 抄六遍。各頁只要給「要截哪一塊」與「叫什麼名字」。
 *
 * 樣式為主要按鈕(藍底白字);旁邊的「列印 / 存 PDF」改為次要的藍框藍字
 * (REQ-20260921003)。
 */
import { useState } from 'react';
import { exportPrintAreasToJpeg } from '../lib/export-jpeg';

export function ExportJpegButton({
  targetRef,
  filename,
  disabled,
  selector,
}: {
  /** 指向包住單據的容器;實際截的是它底下 `selector` 命中的元素 */
  targetRef: React.RefObject<HTMLElement>;
  /** 不含副檔名,`.jpg` 由匯出那支補 */
  filename: string;
  /** 資料還沒回來時比照旁邊的列印按鈕一起鎖住 */
  disabled?: boolean;
  /** 預設 `.print-area`;電子發票證明聯那頁要傳 `.cert` */
  selector?: string;
}): JSX.Element {
  const [busy, setBusy] = useState(false);

  /*
   * 失敗就一句話收場,不做重試 / 不做進度條(CLAUDE.md §2)。
   * 但那句話要先請使用者重新整理(REQ-20260903007):稿樣圖的 presigned 網址只有
   * 5 分鐘有效(`SIGNED_EXPIRES_SEC = 300`,前端 `useAttachmentUrl` 的 staleTime 是
   * 4 分鐘),列印頁擱著超過 5 分鐘再按,圖片會因為網址過期而抓不到 —— 此時畫面上
   * 的圖還看得見(瀏覽器早就載入了),但匯出會失敗,重新整理拿到新網址就好。
   * 重新整理仍然失敗才是真的匯不出來,PDF 那條路本來就還在,直接指過去。
   */
  const run = async (): Promise<void> => {
    setBusy(true);
    try {
      await exportPrintAreasToJpeg(targetRef.current, filename, selector);
    } catch (e) {
      console.error(e);
      alert('JPEG 匯出失敗，請重新整理頁面後再試一次；仍然失敗請改用列印 / 存 PDF');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void run()}
      disabled={busy || disabled}
      title="把畫面上這份單據存成一張 JPEG 圖片（不分頁）"
      className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
      data-testid="export-jpeg-btn"
    >
      {busy ? '產生中…' : '存 JPEG'}
    </button>
  );
}
